import { ApprovalActionRequest, ApprovalStatus } from '@/types/transcriptionSessionTypes';
import { TaskService } from './taskService';
import { TaskWorkflowService } from './taskWorkflowService';
import { TaskType, TaskPriority, TaskStatus } from '@/types/taskTypes';

// Mock approval service - replace with real API calls when backend is ready
export class ApprovalService {
  // Mock data store for development
  private static mockApprovals = new Map<string, {
    status: ApprovalStatus;
    approvedBy?: string;
    approvedAt?: string;
    notes?: string;
  }>();

  /**
   * Approve a transcription session
   */
  static async approveSession(request: ApprovalActionRequest): Promise<void> {
    // TODO: Replace with actual API call
    console.log('Approving session:', request);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock success - store in local state
    this.mockApprovals.set(request.sessionId, {
      status: ApprovalStatus.Approved,
      approvedBy: 'Current User', // TODO: Get from auth context
      approvedAt: new Date().toISOString(),
      notes: request.notes
    });

    // Complete any related approval tasks
    await this.completeApprovalTask(request.sessionId, 'approved', request.notes);
    
    // TODO: Trigger refresh of session data
    window.dispatchEvent(new CustomEvent('approvalStatusChanged', {
      detail: { sessionId: request.sessionId, status: ApprovalStatus.Approved }
    }));
  }

  /**
   * Reject a transcription session
   */
  static async rejectSession(request: ApprovalActionRequest): Promise<void> {
    // TODO: Replace with actual API call
    console.log('Rejecting session:', request);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock success - store in local state
    this.mockApprovals.set(request.sessionId, {
      status: ApprovalStatus.Rejected,
      approvedBy: 'Current User', // TODO: Get from auth context
      approvedAt: new Date().toISOString(),
      notes: request.notes
    });

    // Complete any related approval tasks
    await this.completeApprovalTask(request.sessionId, 'rejected', request.notes);
    
    // TODO: Trigger refresh of session data
    window.dispatchEvent(new CustomEvent('approvalStatusChanged', {
      detail: { sessionId: request.sessionId, status: ApprovalStatus.Rejected }
    }));
  }

  /**
   * Get approval status for a session (mock implementation)
   */
  static getApprovalStatus(sessionId: string): {
    status: ApprovalStatus;
    approvedBy?: string;
    approvedAt?: string;
    notes?: string;
  } {
    return this.mockApprovals.get(sessionId) || {
      status: ApprovalStatus.Pending // Default for demo
    };
  }

  /**
   * Check if current user can approve sessions
   * TODO: Implement proper permission checking
   */
  static async canUserApprove(userId: string, sessionId?: string): Promise<boolean> {
    // Mock implementation - always return true for demo
    // TODO: Replace with actual permission check
    return true;
  }

  /**
   * Get sessions requiring approval
   * TODO: Implement when backend is ready
   */
  static async getSessionsRequiringApproval(): Promise<string[]> {
    // Mock implementation
    return Array.from(this.mockApprovals.keys()).filter(
      sessionId => this.mockApprovals.get(sessionId)?.status === ApprovalStatus.Pending
    );
  }

  /**
   * Set whether a session requires approval
   */
  static async setSessionRequiresApproval(sessionId: string, requiresApproval: boolean): Promise<void> {
    // TODO: Replace with actual API call
    console.log('Setting session requires approval:', { sessionId, requiresApproval });
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (requiresApproval) {
      // If setting to require approval, set status to pending if not already set
      if (!this.mockApprovals.has(sessionId)) {
        this.mockApprovals.set(sessionId, {
          status: ApprovalStatus.Pending
        });
      }
      
      // Create an approval task
      await this.createApprovalTask(sessionId);
    } else {
      // If removing approval requirement, set to not required
      this.mockApprovals.set(sessionId, {
        status: ApprovalStatus.NotRequired
      });
    }
    
    // Trigger refresh of session data
    window.dispatchEvent(new CustomEvent('approvalStatusChanged', {
      detail: { sessionId, requiresApproval }
    }));
  }

  /**
   * Create an approval task for a session
   */
  private static async createApprovalTask(
    sessionId: string, 
    sessionTitle?: string,
    getAccessTokenSilently?: any,
    getAccessTokenWithPopup?: any
  ): Promise<void> {
    try {
      // Check if an approval task already exists for this session
      const existingTasks = await TaskService.getTasks({
        type: [TaskType.Approval],
        status: [TaskStatus.Pending, TaskStatus.InProgress]
      }, undefined, 1, 50, getAccessTokenSilently, getAccessTokenWithPopup);
      
      // Filter by sessionId
      const sessionTasks = existingTasks.tasks.filter(task => task.sessionId === sessionId);
      
      if (sessionTasks.length > 0) {
        // Task already exists, don't create duplicate
        return;
      }

      // Create new approval task with better title
      const taskTitle = sessionTitle 
        ? `Review and approve: ${sessionTitle}`
        : `Review and approve session`;

      await TaskService.createTask({
        title: taskTitle,
        description: `This transcription session requires approval before it can be processed further. Please review the content and approve or reject accordingly.`,
        type: TaskType.Approval,
        priority: TaskPriority.High,
        sessionId: sessionId,
        relatedRecord: {
          id: sessionId,
          type: 'session',
          title: sessionTitle || `Session ${sessionId}`,
          url: `/transcript/${sessionId}`
        },
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Due in 24 hours
        tags: ['approval', 'high-priority']
      }, getAccessTokenSilently, getAccessTokenWithPopup);
    } catch (error) {
      console.error('Failed to create approval task:', error);
    }
  }

  /**
   * Complete approval task when session is approved/rejected
   */
  private static async completeApprovalTask(sessionId: string, action: 'approved' | 'rejected', notes?: string): Promise<void> {
    try {
      // Find the approval task for this session
      const existingTasks = await TaskService.getTasks({
        type: [TaskType.Approval],
        status: [TaskStatus.Pending, TaskStatus.InProgress]
      });
      
      // Filter by sessionId and complete the tasks
      const sessionTasks = existingTasks.tasks.filter(task => task.sessionId === sessionId);
      
      for (const task of sessionTasks) {
        await TaskService.updateTask({
          id: task.id,
          status: TaskStatus.Completed,
          notes: notes ? `Session ${action}: ${notes}` : `Session ${action}`
        });
      }
    } catch (error) {
      console.error('Failed to complete approval task:', error);
    }
  }
}

// Export individual functions for easier importing
export const approveSession = ApprovalService.approveSession.bind(ApprovalService);
export const rejectSession = ApprovalService.rejectSession.bind(ApprovalService);
export const getApprovalStatus = ApprovalService.getApprovalStatus.bind(ApprovalService);
export const canUserApprove = ApprovalService.canUserApprove.bind(ApprovalService);
export const getSessionsRequiringApproval = ApprovalService.getSessionsRequiringApproval.bind(ApprovalService);
export const setSessionRequiresApproval = ApprovalService.setSessionRequiresApproval.bind(ApprovalService);
